/**
 * PDF-Vorschau (erste Seite) – client-seitig mit PDF.js.
 *
 * PDF.js wird NICHT in child.js gebundelt (zu gross), sondern als Vendor-Datei
 * unter src/js/addons/pdfjs/ ausgeliefert (gulp `jsAddons` kopiert sie nach
 * dist/js/addons/pdfjs/) und erst bei Bedarf per <script> nachgeladen. Der
 * Worker-Pfad wird aus dem Script-Src abgeleitet, damit der Layout-Pfad nicht
 * mehrfach hartkodiert ist.
 *
 * queuePdfPreview(url) -> Promise<HTMLCanvasElement|HTMLImageElement>
 * Rendert die erste Seite in ein <canvas> (feste Breite RENDER_WIDTH). Es wird
 * NICHT die komplette PDF heruntergeladen: disableAutoFetch + disableStream
 * (beide zwingend zusammen, siehe renderPdfPreview) laden per
 * HTTP-Range-Request nur die fuer Seite 1 noetigen Chunks. Funktioniert nur,
 * wenn die PDF same-origin bzw. CORS-frei erreichbar ist (userdata-PDFs sind
 * same-origin). Laeuft ueber eine Warteschlange mit Konkurrenz-Limit (siehe
 * unten), damit nicht beliebig viele PDF.js-Ladevorgaenge (je eigener Web
 * Worker) parallel laufen.
 *
 * Das Ergebnis wird zweifach wiederverwendet:
 *  1. Lokal per Cache Storage API (ueberlebt Reload/neuen Tab): beim naechsten
 *     Besuch liefert ein Treffer ein fertiges <img>, ohne PDF.js nachzuladen.
 *  2. Global via Backend-Extension pdfThumbnails: das gerenderte PNG wird
 *     fire-and-forget zu /em/pdfThumbnails/store hochgeladen. Der Server kann
 *     PDFs nicht selbst rastern (kein Ghostscript); ist das Thumbnail einmal
 *     gespeichert, rendert collection_downloads.php fuer ALLE weiteren
 *     Besucher direkt ein statisches <img> und dieser Code hier laeuft fuer
 *     die Datei gar nicht mehr.
 *
 * Vanilla JS, kein jQuery.
 */

import { FetchService } from 'dc-services/fetch.service';

// Pfad zum PDF.js-UMD-Build (relativ zur Layout-Auslieferung).
const PDFJS_SRC = '/layout/frontend/alferproline/dist/js/addons/pdfjs/pdf.min.js';

// Feste Render-Breite in Device-Pixeln (unabhaengig von CSS-Breite/DPR):
// so ist das Ergebnis kanonisch und kann als serverseitiges Thumbnail fuer
// alle Geraete wiederverwendet werden (Anzeige skaliert per CSS herunter).
const RENDER_WIDTH = 300;

let pdfjsReady = null;

function loadPdfJs() {
    if (pdfjsReady) return pdfjsReady;

    pdfjsReady = new Promise((resolve, reject) => {
        if (window.pdfjsLib) {
            resolve(window.pdfjsLib);
            return;
        }
        const script = document.createElement('script');
        script.src = PDFJS_SRC;
        script.async = true;
        script.onload = () => {
            if (!window.pdfjsLib) {
                reject(new Error('pdfjsLib nicht verfügbar'));
                return;
            }
            // Worker-Pfad aus dem eigenen Script-Src ableiten.
            const base = PDFJS_SRC.replace(/pdf\.min\.js$/, '');
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = base + 'pdf.worker.min.js';
            resolve(window.pdfjsLib);
        };
        script.onerror = () => reject(new Error('PDF.js konnte nicht geladen werden'));
        document.head.appendChild(script);
    });

    return pdfjsReady;
}

async function renderPdfPreview(url) {
    const lib = await loadPdfJs();

    // disableStream MUSS true sein: mit aktivem Streaming laedt PDF.js die
    // komplette Datei im Hintergrund weiter und disableAutoFetch ist wirkungslos.
    // Nur die Kombination aus beiden sorgt dafuer, dass ausschliesslich die per
    // Range-Request benoetigten Chunks (Header, xref, Seite-1-Objekte) geladen
    // werden. Voraussetzung: Server liefert Accept-Ranges (nginx-userdata: ja).
    const loadingTask = lib.getDocument({
        url,
        disableAutoFetch: true,
        disableStream: true,
        rangeChunkSize: 64 * 1024,
    });
    const pdf = await loadingTask.promise;
    const page = await pdf.getPage(1);

    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: RENDER_WIDTH / base.width });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');

    await page.render({ canvasContext: ctx, viewport }).promise;

    // Speicher freigeben.
    pdf.cleanup();
    loadingTask.destroy();

    return canvas;
}

// Persistenter Thumbnail-Cache (Cache Storage API) ueber Page-Reloads/Sessions
// hinweg. Cache Storage braucht einen sicheren Kontext (https/localhost) - wo
// nicht verfuegbar, wird einfach immer neu gerendert (kein harter Fehler).
// v2: feste Render-Breite, Cache-Key ohne CSS-Breite/DPR.
const PREVIEW_CACHE_NAME = 'downloads-pdf-preview-v2';
const PREVIEW_CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 Tage

function buildPreviewCacheKey(url) {
    const key = new URL(url, window.location.href);
    key.searchParams.set('previewW', RENDER_WIDTH);
    return key.href;
}

// Bewusst data:-URL statt URL.createObjectURL(): die Content-Security-Policy
// der Seite erlaubt in img-src zwar 'data:', aber KEIN 'blob:' - eine
// Objekt-URL wird vom Browser blockiert (img.onerror) und der Cache-Treffer
// wuerde verworfen, sodass bei jedem Besuch neu gerendert wird.
function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Thumbnail-Blob konnte nicht gelesen werden'));
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blob);
    });
}

async function blobToImg(blob) {
    const dataUrl = await blobToDataUrl(blob);
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Cache-Thumbnail konnte nicht geladen werden'));
        img.src = dataUrl;
    });
}

function canvasToBlob(canvas) {
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
}

async function readPreviewBlobFromCache(cacheKey) {
    if (typeof caches === 'undefined') return null;
    try {
        const cache = await caches.open(PREVIEW_CACHE_NAME);
        const response = await cache.match(cacheKey);
        if (!response) return null;

        const cachedAt = Number(response.headers.get('x-cached-at') || 0);
        if (!cachedAt || Date.now() - cachedAt > PREVIEW_CACHE_MAX_AGE_MS) {
            await cache.delete(cacheKey);
            return null;
        }

        return await response.blob();
    } catch {
        return null; // Cache Storage nicht verfuegbar/fehlerhaft - unkritisch
    }
}

async function writePreviewToCache(cacheKey, blob) {
    if (typeof caches === 'undefined') return;
    try {
        const cache = await caches.open(PREVIEW_CACHE_NAME);
        await cache.put(cacheKey, new Response(blob, {
            headers: { 'Content-Type': 'image/png', 'x-cached-at': String(Date.now()) },
        }));
    } catch {
        /* Cache Storage nicht verfuegbar oder Quota erreicht - unkritisch */
    }
}

// Gerendertes Thumbnail zum Server hochladen (Backend-Extension pdfThumbnails),
// damit alle weiteren Besucher ein statisches Bild bekommen. Wird nur
// aufgerufen, wenn client-seitig gerendert werden musste - existiert das
// Server-Thumbnail bereits, rendert das Template direkt ein <img> und dieser
// Code laeuft nicht. Fire-and-forget: Fehler sind unkritisch, der naechste
// Besucher versucht es erneut.
const uploadedThumbs = new Set();

async function uploadThumbnail(url, blob) {
    const fileName = decodeURIComponent((url.split('?')[0].split('#')[0].split('/').pop() || ''));
    if (fileName === '' || uploadedThumbs.has(fileName)) return;
    uploadedThumbs.add(fileName);

    try {
        const png = await blobToDataUrl(blob);
        await FetchService.post('/em/pdfThumbnails/store', { file: fileName, png });
    } catch {
        /* unkritisch - naechster Besucher laedt erneut hoch */
    }
}

async function getOrRenderPreview(url) {
    const cacheKey = buildPreviewCacheKey(url);

    const cachedBlob = await readPreviewBlobFromCache(cacheKey);
    if (cachedBlob) {
        // Auch bei lokalem Cache-Treffer hochladen: dass dieser Code laeuft,
        // heisst, dass das Server-Thumbnail (noch) fehlt.
        uploadThumbnail(url, cachedBlob);
        return blobToImg(cachedBlob);
    }

    const canvas = await renderPdfPreview(url);
    canvasToBlob(canvas).then(blob => {
        if (!blob) return;
        writePreviewToCache(cacheKey, blob); // im Hintergrund, blockiert die Anzeige nicht
        uploadThumbnail(url, blob);
    });

    return canvas;
}

// Begrenzt gleichzeitige PDF.js-Ladevorgaenge (je eigener Web Worker +
// eigene Range-Requests): ohne Limit konkurrieren beim Lazy-Reveal viele
// Karten auf einmal um Bandbreite/CPU und bremsen sich gegenseitig aus.
const MAX_CONCURRENT_PREVIEWS = 3;
let activePreviews = 0;
const previewQueue = [];

function runPreviewQueue() {
    if (activePreviews >= MAX_CONCURRENT_PREVIEWS || previewQueue.length === 0) return;

    const { url, resolve, reject } = previewQueue.shift();
    activePreviews++;
    getOrRenderPreview(url)
        .then(resolve, reject)
        .finally(() => {
            activePreviews--;
            runPreviewQueue();
        });
}

export function queuePdfPreview(url) {
    return new Promise((resolve, reject) => {
        previewQueue.push({ url, resolve, reject });
        runPreviewQueue();
    });
}
